import React, { useState, useEffect } from 'react'
import { Tooltip } from 'antd'
import { IconPlusRounded, IconMinusRounded } from '@edulastic/icons'
import {
  MasteryPercentagePill,
  TwoCardContainer,
  StandardName,
  StandardDescription,
  SubGroupTwoColumnLayout,
  StudentListText,
  StudentListExpandButton,
  StudentListExpandLabel,
} from './styled'
import RecommendedFollowUps from './RecommendedFollowUps'
import { GROUP_AND_ASSIGN_SUBGROUP_STUDENT_DISPLAY_MAX } from './constants'

const SubGroupCard = ({
  subGroup,
  followUps,
  group,
  isMastered,
  isLowestBand = false,
  recommendedTestsLoading,
  recommendedDecksLoading,
  soloSingleStudentView = false,
  includeTestContext = false,
  defaultGrades = [],
  defaultSubjects = [],
  curriculumId = null,
}) => {
  const {
    students = [],
    identifier = '',
    desc = '',
    scorePercentage,
    bgColor: subGroupBgColor,
    textColor: subGroupTextColor,
  } = subGroup

  const pillBgColor = subGroupBgColor ?? group?.bgColor
  const pillTextColor = subGroupTextColor ?? group?.textColor

  const studentNameList = students
    .map((s) => s.name || s.studentName || '')
    .filter(Boolean)
  const studentNamesStrFull = studentNameList.join(', ')
  const subgroupNameMax = GROUP_AND_ASSIGN_SUBGROUP_STUDENT_DISPLAY_MAX
  const truncatedByStudentCount = studentNameList.length > subgroupNameMax

  const studentCount = students.length

  const [isExpanded, setIsExpanded] = useState(false)

  const studentListDisplayStr =
    studentNameList.length === 0
      ? '—'
      : isExpanded
      ? studentNamesStrFull
      : studentNameList.slice(0, subgroupNameMax).join(', ')

  useEffect(() => {
    setIsExpanded(false)
  }, [identifier, studentNamesStrFull])

  const showStudentListToggle =
    !soloSingleStudentView &&
    studentNameList.length > 0 &&
    (isExpanded || truncatedByStudentCount)

  return (
    <TwoCardContainer>
      <StandardName>
        {identifier || 'Standard'}:{' '}
        <Tooltip title={desc} placement="top">
          <StandardDescription as="span">{desc}</StandardDescription>
        </Tooltip>
      </StandardName>
      <SubGroupTwoColumnLayout>
        <div style={{ marginBottom: '20px' }}>
          {scorePercentage != null && (
            <p style={{ color: '#151D34' }}>
              {soloSingleStudentView ? (
                <>
                  Average mastery:{' '}
                  <MasteryPercentagePill
                    $bgColor={pillBgColor}
                    $textColor={pillTextColor}
                  >
                    {Math.round(scorePercentage)}%
                  </MasteryPercentagePill>
                </>
              ) : (
                <>
                  Avg mastery of {studentCount} student
                  {studentCount === 1 ? '' : 's'}:{' '}
                  <MasteryPercentagePill
                    $bgColor={pillBgColor}
                    $textColor={pillTextColor}
                  >
                    {Math.round(scorePercentage)}%
                  </MasteryPercentagePill>
                </>
              )}
            </p>
          )}
          {!soloSingleStudentView && (
            <>
              <StudentListText
                $clamped={truncatedByStudentCount && !isExpanded}
              >
                {studentListDisplayStr}
              </StudentListText>
              {showStudentListToggle && (
                <StudentListExpandButton
                  type="button"
                  onClick={() => setIsExpanded((v) => !v)}
                >
                  <span aria-hidden style={{ display: 'inline-flex' }}>
                    {isExpanded ? (
                      <IconMinusRounded
                        width={14}
                        height={14}
                        color="#595959"
                      />
                    ) : (
                      <IconPlusRounded width={14} height={14} color="#595959" />
                    )}
                  </span>
                  <StudentListExpandLabel>
                    {isExpanded ? 'See less students' : 'See more students'}
                  </StudentListExpandLabel>
                </StudentListExpandButton>
              )}
            </>
          )}
        </div>
        <div>
          <RecommendedFollowUps
            followUps={followUps}
            group={group}
            subGroup={subGroup}
            isMastered={isMastered}
            isLowestBand={isLowestBand}
            recommendedTestsLoading={recommendedTestsLoading}
            recommendedDecksLoading={recommendedDecksLoading}
            includeTestContext={includeTestContext}
            defaultGrades={defaultGrades}
            defaultSubjects={defaultSubjects}
            curriculumId={curriculumId}
          />
        </div>
      </SubGroupTwoColumnLayout>
    </TwoCardContainer>
  )
}

export default SubGroupCard
